import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useLoad } from '../hooks';
import { PageHead } from '../Layout';
import { formatDateTime, formatRelative, formatRunDuration, money } from '../format';
import { RunPanel } from '../components/RunPanel';
import { Icon } from '../components/Icon';
import type { AgentInfo, RunSummary } from '../../shared/types';

export default function Activity() {
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [agents, setAgents] = useState<string[]>([]);
  const [agent, setAgent] = useState('');
  const [open, setOpen] = useState<Set<number>>(new Set());

  const { loading, error } = useLoad(
    useCallback(async () => {
      const q = agent ? `?agent=${encodeURIComponent(agent)}&limit=200` : '?limit=200';
      const res = await api.get<{ items: RunSummary[] }>(`/runs${q}`);
      setRuns(res.items);
    }, [agent]),
    { pollMs: 10_000 },
  );

  // The filter options come from the agent registry; a failure here just
  // leaves the dropdown with "all" — the run list surfaces its own errors.
  useEffect(() => {
    api
      .get<{ items: AgentInfo[] }>('/agents')
      .then((res) => setAgents(res.items.map((a) => a.slug)))
      .catch(() => {});
  }, []);

  function toggle(id: number) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const failedRuns = runs.filter((run) => run.status === 'failed').length;
  const runningRuns = runs.filter((run) => run.status === 'running').length;

  return (
    <>
      <PageHead title="Activity" subtitle="The operational record for archive processing and legacy automation." />

      <div className="activity-toolbar">
        <label>
          <span>Agent</span>
          <select value={agent} onChange={(e) => setAgent(e.target.value)}>
          <option value="">All agents</option>
          {agents.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
          </select>
        </label>
        <dl className="activity-summary">
          <div><dt>Loaded</dt><dd>{runs.length}</dd></div>
          <div><dt>Running</dt><dd>{runningRuns}</dd></div>
          <div className={failedRuns > 0 ? 'has-failures' : ''}><dt>Failed</dt><dd>{failedRuns}</dd></div>
        </dl>
      </div>

      {error ? <p className="fail">{error}</p> : null}

      {loading ? (
        <div className="empty">Loading…</div>
      ) : runs.length === 0 ? (
        <div className="empty">Awaiting data — no runs recorded yet.</div>
      ) : (
        <div className="table-scroll">
          <table className="run-table">
            <thead>
              <tr>
                <th />
                <th>Run</th>
                <th>Agent</th>
                <th>Status</th>
                <th>Trigger</th>
                <th>Started</th>
                <th>Took</th>
                <th>Tokens</th>
                <th>Model estimate</th>
                <th>Summary</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => {
                const isOpen = open.has(r.id);
                return (
                  <Fragment key={r.id}>
                    <tr
                      className="run-row"
                      onClick={() => toggle(r.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          toggle(r.id);
                        }
                      }}
                      tabIndex={0}
                      aria-expanded={isOpen}
                    >
                      <td className="run-chevron">
                        <Icon className={`chevron${isOpen ? ' open' : ''}`} name="chevron" />
                      </td>
                      <td className="mono">
                        <Link to={`/activity/${r.id}`} onClick={(e) => e.stopPropagation()}>
                          {r.id}
                        </Link>
                      </td>
                      <td>{r.agentSlug}</td>
                      <td>
                        <span className={`chip${r.status === 'failed' ? ' bad' : r.status === 'running' ? ' on' : ''}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="muted">{r.trigger}</td>
                      <td className="mono faint" title={formatDateTime(r.startedAt)}>
                        {formatRelative(r.startedAt)}
                      </td>
                      <td className="mono">{formatRunDuration(r)}</td>
                      <td className="mono">
                        {r.tokensIn === null && r.tokensOut === null
                          ? '—'
                          : `${r.tokensIn ?? 0}/${r.tokensOut ?? 0}`}
                      </td>
                      <td className="mono">{money(r.costUsd)}</td>
                      <td className={r.error ? 'fail' : 'muted'}>
                        {(r.error ?? r.summary ?? '').split('\n')[0]?.slice(0, 90) || '—'}
                      </td>
                    </tr>
                    {isOpen ? (
                      <tr className="run-row-detail">
                        <td colSpan={10}>
                          <RunPanel run={r} />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
